/**
 * The brand lockup: mark, brand, module label.
 * @file src/web/shared/BrandLockup.tsx
 *
 *     [mark]  Naumene  ⟨CORE⟩
 *
 * One component rather than the three near-identical copies the scaffold had
 * in AppLayoutRoute, DashboardLayoutRoute and AuthLayoutRoute — brand.ts calls
 * itself "one source of brand truth", and three hand-rolled lockups made that
 * untrue the moment one of them changed.
 *
 * ── Why the module is a label and not part of the name ────────────────────
 * Every app in the suite carries the same brand; only the label changes. A
 * user crossing from Core to Matters through SSO should see one product with
 * a different section, not two products. Rendering "Naumene Matters" as a
 * single string makes them look like separate purchases, which is the exact
 * impression the platform is built to avoid.
 *
 * The kit reserves caps for "small eyebrow labels" — this is one, which is why
 * it is uppercase where the rest of the UI is sentence case.
 *
 * @llm-rule WHEN: Rendering the logo + name anywhere in a shell
 * @llm-rule AVOID: Writing the brand and module as one string — use
 *   `brand.productName` for prose, this component for the lockup
 */

import { Link } from 'react-router-dom';
import { brand } from './brand';
import { NaumeneMark } from './NaumeneMark';

export function BrandLockup({
  className = '',
  /** Hide the wordmark and label — for a collapsed sidebar rail. */
  markOnly = false,
  /** Wrap in a link home. Off inside a nav that already links. */
  asLink = true,
  /**
   * Show the module label, when there is one. There is not, today: the
   * product IS the brand, so the badge renders nothing. Kept for the day a
   * second module exists and a signed-in user needs to know which one they
   * are looking at.
   */
  showModule = true,
}: {
  className?: string;
  markOnly?: boolean;
  asLink?: boolean;
  showModule?: boolean;
}) {
  const content = (
    <>
      <NaumeneMark />
      {!markOnly && (
        <span className="flex min-w-0 items-center gap-2">
          {/* Kit lockup spec is Inter 700 at -0.01em; taken to 800 here because
            * the wordmark sits at 16px in a header rather than the kit's 44px,
            * and the extra weight is what keeps it reading as a mark at that
            * size. Tracking stays the kit's -0.01em — NOT Tailwind's
            * `tracking-tight`, which is -0.025em and tighter than specified.
            *
            * One colour. A two-tone split was tried and dropped: gold is the
            * kit's accent for the connector dot and small highlights, and
            * carrying it across half the wordmark made the mark compete with
            * itself — the dot stops being the one gold thing on screen. */}
          <span className="truncate font-extrabold tracking-[-0.01em]">{brand.name}</span>
          {/* The module, not a second brand: a small filled gold badge, which
            * is the kit's "small highlight" rather than the large fill it
            * rules out. Colours come from tokens so the pair stays together
            * and stays contrast-checked. */}
          {showModule && brand.module && (
            <span
              className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
              style={{
                backgroundColor: 'var(--logo-label-bg)',
                color: 'var(--logo-label-fg)',
              }}
            >
              {brand.module}
            </span>
          )}
        </span>
      )}
    </>
  );

  const classes = `flex items-center gap-2.5 ${className}`;

  return asLink ? (
    <Link to="/" className={classes}>
      {content}
    </Link>
  ) : (
    <span className={classes}>{content}</span>
  );
}
